import { useRef, useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { showError } from "../../lib/ReactToastify";

interface MapComponentProps {
  setLat: (lat: number) => void;
  setLng: (lng: number) => void;
  setGeoLocation?: (geoLocation: string) => void; // Optional callback to set the address
  lat?: number;
  lng?: number;
  isDialog?: boolean;
  height?: string;
}

const MapComponent = ({
  setLat,
  setLng,
  setGeoLocation,
  isDialog,
  height,
  lat,
  lng,
}: MapComponentProps) => {
  const [position, setPosition] = useState<any>(
    lat && lng ? [lat, lng] : [25.276987, 55.296249]
  ); // Default coordinates
  const [address, setAddressState] = useState<string>(""); // Address state
  const mapRef = useRef<L.Map | null>(null);

  const redMarkerIcon = new L.Icon({
    iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    className: "red-marker",
  });

  const getCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          updatePosition(latitude, longitude);
        },
        (error) => {
          console.error("Error getting location:", error);
          if (error.code === error.PERMISSION_DENIED) {
            alert(
              "Location access denied. Please allow access in your browser settings."
            );
          } else {
            showError(
              "An unknown error occurred while obtaining the location."
            );
          }
        }
      );
    }
  };

  const fetchAddress = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (data && data.display_name) {
        const fetchedAddress = data.display_name;
        setAddressState(fetchedAddress);
        if (setGeoLocation) setGeoLocation(fetchedAddress); // Optional callback
      } else {
        setAddressState("Address not found");
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setAddressState("Error fetching address");
    }
  };

  const updatePosition = (lat: number, lng: number) => {
    setPosition([lat, lng]);
    setLat(lat);
    setLng(lng);
    fetchAddress(lat, lng); // Fetch the address
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], 13);
    }
  };

  useEffect(() => {
    if (position[0] === 25.276987 && position[1] === 55.296249) {
      getCurrentPosition();
    } else {
      fetchAddress(position[0], position[1]);
    }
  }, []);

  const MapEvents = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        updatePosition(lat, lng);
      },
    });
    return null;
  };

  return (
    <div>
      <MapContainer
        center={position}
        zoom={13}
        ref={mapRef}
        style={{
          height: height ? height : "130px",
          borderRadius: "12px",
          border: "1px solid #ccc",
          zIndex: 0,
        }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        <Marker position={position} icon={redMarkerIcon} />
        <MapEvents />
      </MapContainer>
      {address && <p>Address: {address}</p>}
    </div>
  );
};

export default MapComponent;
